import { Controller } from "@hotwired/stimulus"
import {
  clearPercentField,
  hasDiscount,
  parseDiscountPercent,
  revealLockReason,
  syncGrossAmountLock,
  validateAndClampPercent
} from "../../utils/discount.util"
import { formatNumber, getCurrencyDecimals, getUserLocale, parseInputNumber } from "../../utils/number-format.util"

// Connects to data-controller="documents--discount"
// Manages document-level discount calculations
export default class extends Controller {
  static outlets = ["documents--totals-calculator"]

  // lockReason: why the EUR toggle is locked in gross mode, shown beside it. An expense's summary only.
  static targets = [
    "percent",
    "amount",
    "description",
    "toggle",
    "typeToggle",
    "togglePercent",
    "toggleAmount",
    "lockReason"
  ]

  static values = {
    defaultDescription: String,
    discountType: { type: String, default: "percentage" },
    currency: { type: String, default: "EUR" },
    // Why the EUR toggle is locked in gross mode, shown as its title. An expense's summary only.
    grossModeTitle: String
  }

  connect() {
    // Detect initial mode from server-rendered values
    if (this.hasAmountTarget && this.hasPercentTarget) {
      const locale = getUserLocale()
      const amountVal = parseInputNumber(this.amountTarget, locale) || 0
      const percentVal = this.percentTarget.value?.trim()
      if (
        amountVal > 0 &&
        (!percentVal || percentVal === "" || parseDiscountPercent(this.percentTarget, locale) === 0)
      ) {
        this.discountTypeValue = "amount"
      }
    }
    this.#syncVisibility()
    this.syncGrossMode()

    // Track document currency: the [%|EUR] toggle button must follow the
    // currency selector so renaming EUR → CZK live updates the chip text.
    this.documentCurrencySelect = document.querySelector("#currency")
    if (this.documentCurrencySelect) {
      this.currencyValue = this.documentCurrencySelect.value || this.currencyValue
      this._onDocumentCurrencyChange = (event) => {
        this.currencyValue = event.target.value
        this.#syncCurrencyLabel()
        this.#updateDescription()
      }
      this.documentCurrencySelect.addEventListener("change", this._onDocumentCurrencyChange)
    }
    this.#syncCurrencyLabel()

    // Notify totals calculator if there's an existing discount on load
    if (this.#hasAnyDiscount()) {
      this.#notifyTotalsCalculator()
    }
  }

  disconnect() {
    if (this.documentCurrencySelect && this._onDocumentCurrencyChange) {
      this.documentCurrencySelect.removeEventListener("change", this._onDocumentCurrencyChange)
      this._onDocumentCurrencyChange = null
    }
  }

  // The summary's calculator may connect after this block; its mode is read once it is there.
  documentsTotalsCalculatorOutletConnected() {
    this.syncGrossMode()
  }

  // Public API: gross_per_rate takes percentage discounts only (spec NG5), the document's as well as a
  // line's (documents--item-discount#syncGrossMode): while the expense is calculated per gross the
  // EUR toggle is locked and toggleDiscountType refuses the amount; the lock is syncGrossAmountLock's.
  // The totals calculator calls this when the mode changes (documents--totals-calculator#vatModeValueChanged).
  syncGrossMode() {
    const gross = this.#isGrossMode()
    const backToPercentage = syncGrossAmountLock({
      gross,
      toggle: this.hasToggleAmountTarget ? this.toggleAmountTarget : null,
      title: this.grossModeTitleValue,
      reason: this.hasLockReasonTarget ? this.lockReasonTarget : null,
      isAmount: this.discountTypeValue === "amount",
      amountInput: this.hasAmountTarget ? this.amountTarget : null
    })
    // The document's reason is shown beside the toggle, inside its live region, while it is locked.
    if (this.hasLockReasonTarget) this.lockReasonTarget.classList.toggle("d-none", !gross)

    if (backToPercentage) {
      this.discountTypeValue = "percentage"
      this.#syncVisibility()
    }
  }

  #isGrossMode() {
    return this.hasDocumentsTotalsCalculatorOutlet && this.documentsTotalsCalculatorOutlet.isGrossMode()
  }

  onDiscountPercentChange() {
    const locale = getUserLocale()
    // Parse and validate percentage value
    let discountPercent = parseDiscountPercent(this.percentTarget, locale)
    discountPercent = validateAndClampPercent(this.percentTarget, discountPercent, locale)

    // Clear amount field — mutual exclusivity
    if (this.hasAmountTarget) {
      this.amountTarget.value = ""
    }

    this.#updateDescription()
    // Notify totals calculator to recalculate
    this.#notifyTotalsCalculator()
  }

  onDiscountAmountChange() {
    const locale = getUserLocale()
    let amount = parseInputNumber(this.amountTarget, locale) || 0
    const discountable = this.#getDiscountableAmount()

    // Clamp: amount cannot exceed what it is a discount of, or be negative
    if (amount < 0) {
      amount = 0
      this.amountTarget.value = "0"
    } else if (discountable > 0 && amount > discountable) {
      amount = discountable
      this.amountTarget.value = formatNumber(discountable, locale, this.#currencyFractionOptions())
    }

    // Clear percentage field — mutual exclusivity
    if (this.hasPercentTarget) {
      this.percentTarget.value = ""
    }

    this.syncGrossMode()
    this.#updateDescription()
    // Notify totals calculator to recalculate
    this.#notifyTotalsCalculator()
  }

  toggleDiscountType(event) {
    const clickedType = event.currentTarget.dataset.type
    if (clickedType === this.discountTypeValue) return
    // gross_per_rate: percentages only. The refused tap says why: the reason shown beside the toggle
    // already (revealLockReason leaves it as it is).
    if (clickedType === "amount" && this.#isGrossMode()) {
      revealLockReason(this.hasLockReasonTarget ? this.lockReasonTarget : null)
      return
    }

    const locale = getUserLocale()
    const discountable = this.#getDiscountableAmount()

    if (clickedType === "amount") {
      // Switching % -> amount: pre-fill computed amount from percentage
      if (this.hasPercentTarget && hasDiscount(this.percentTarget, locale)) {
        const discountPercent = parseDiscountPercent(this.percentTarget, locale)
        const computedAmount = (discountable / 100) * discountPercent
        if (this.hasAmountTarget) {
          this.amountTarget.value =
            computedAmount > 0 ? formatNumber(computedAmount, locale, this.#currencyFractionOptions()) : ""
        }
      }
      if (this.hasPercentTarget) {
        this.percentTarget.value = ""
      }
    } else {
      // Switching amount -> %: pre-fill computed percentage from amount. In gross mode (the page that
      // refused the amount) it is a share of the gross, as the percentage then is.
      if (this.hasAmountTarget && (parseInputNumber(this.amountTarget, locale) || 0) > 0) {
        const amount = parseInputNumber(this.amountTarget, locale) || 0
        const computedPercent = discountable > 0 ? (amount / discountable) * 100 : 0
        // In the user's format, not toFixed(2): parseInputNumber reads a value a
        // controller wrote with the locale, and de-DE would take "12.50" as 1250.
        if (this.hasPercentTarget) {
          this.percentTarget.value =
            computedPercent > 0
              ? formatNumber(computedPercent, locale, {
                  style: "decimal",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2
                })
              : ""
        }
      }
      if (this.hasAmountTarget) {
        this.amountTarget.value = ""
      }
    }

    this.discountTypeValue = clickedType
    this.#syncVisibility()
    this.#updateDescription()
    this.#notifyTotalsCalculator()
  }

  // Public API: Show discount section and restore cached values if available
  show() {
    const locale = getUserLocale()
    // gross_per_rate takes no amount, so one hidden before the switch to gross is not brought back.
    const cachedAmount = this.#isGrossMode() ? "" : this._cachedAmount
    const hasCached = cachedAmount || this._cachedPercent
    const currentlyEmpty =
      (!this.hasAmountTarget || !this.amountTarget.value || (parseInputNumber(this.amountTarget, locale) || 0) === 0) &&
      (!this.hasPercentTarget || !this.percentTarget.value || this.percentTarget.value.trim() === "")

    if (hasCached && currentlyEmpty) {
      // Restore previously cached values from hide()
      if (this.hasPercentTarget && this._cachedPercent) {
        this.percentTarget.value = this._cachedPercent
      }
      if (this.hasAmountTarget && cachedAmount) {
        this.amountTarget.value = cachedAmount
      }
      if (this.hasDescriptionTarget && this._cachedDescription) {
        this.descriptionTarget.value = this._cachedDescription
      }
      // Restore cached discount type
      if (this._cachedDiscountType) {
        this.discountTypeValue = this._cachedDiscountType
      }
    } else {
      // First time enabling — clear all discount values to start fresh
      if (this.hasPercentTarget) {
        clearPercentField(this.percentTarget, locale)
      }
      if (this.hasAmountTarget) {
        this.amountTarget.value = ""
      }
      if (this.hasDescriptionTarget) {
        // Set default description if field is empty
        if (!this.descriptionTarget.value || this.descriptionTarget.value.trim() === "") {
          this.descriptionTarget.value = this.hasDefaultDescriptionValue ? this.defaultDescriptionValue : ""
        }
      }
      // Default to percentage mode on fresh show
      this.discountTypeValue = "percentage"
    }
    this.#syncVisibility()
    this.element.classList.remove("d-none")
    // Sync toggle state
    this.#syncToggleState(true)
    // Notify totals calculator to recalculate and show discount in summary
    this.#notifyTotalsCalculator()
    // Focus on discount description field
    if (this.hasDescriptionTarget) {
      this.descriptionTarget.focus()
    }
  }

  // Public API: Hide discount section, cache values, then clear all fields
  hide() {
    const locale = getUserLocale()
    // Cache current values before clearing so show() can restore them
    if (this.hasAmountTarget) {
      this._cachedAmount = this.amountTarget.value
    }
    if (this.hasPercentTarget) {
      this._cachedPercent = this.percentTarget.value
    }
    if (this.hasDescriptionTarget) {
      this._cachedDescription = this.descriptionTarget.value
    }
    this._cachedDiscountType = this.discountTypeValue
    if (this.hasPercentTarget) {
      clearPercentField(this.percentTarget, locale)
      // Clear the actual form field value to empty string so it's not submitted
      this.percentTarget.value = ""
    }
    if (this.hasAmountTarget) {
      // Set to empty string so it's not submitted (empty string will be nil in Rails)
      this.amountTarget.value = ""
    }
    if (this.hasDescriptionTarget) {
      this.descriptionTarget.value = ""
    }
    this.element.classList.add("d-none")
    // Sync toggle state
    this.#syncToggleState(false)
    // Notify totals calculator to recalculate
    this.#notifyTotalsCalculator()
  }

  // Handle toggle change event
  toggleDiscount(event) {
    if (event.target.checked) {
      this.show()
    } else {
      this.hide()
    }
  }

  // Public API: Get discount percentage for totals calculator
  getDiscountPercent() {
    if (this.discountTypeValue !== "percentage") return 0
    const locale = getUserLocale()
    if (!this.hasPercentTarget || !hasDiscount(this.percentTarget, locale)) {
      return 0
    }
    return parseDiscountPercent(this.percentTarget, locale)
  }

  // Public API: Get total discount amount for totals calculator
  // Returns positive value (discount amount to subtract)
  getTotalDiscount() {
    if (this.discountTypeValue === "amount") {
      // Amount mode: return the amount directly
      if (this.hasAmountTarget) {
        const locale = getUserLocale()
        const amount = parseInputNumber(this.amountTarget, locale) || 0
        return amount > 0 ? amount : 0
      }
      return 0
    }
    // Percentage mode: calculate amount from percentage
    const locale = getUserLocale()
    if (this.hasPercentTarget && hasDiscount(this.percentTarget, locale)) {
      const discountPercent = parseDiscountPercent(this.percentTarget, locale)
      return (this.#getDiscountableAmount() / 100) * discountPercent
    }
    return 0
  }

  // What the document discount is a share of, before it is applied: the subtotal, or the lines' gross
  // in gross_per_rate (documents--totals-calculator#getDiscountableAmount).
  #getDiscountableAmount() {
    if (this.hasDocumentsTotalsCalculatorOutlet) {
      return this.documentsTotalsCalculatorOutlet.getDiscountableAmount()
    }
    return 0
  }

  // Notify totals calculator to recalculate
  #notifyTotalsCalculator() {
    if (this.hasDocumentsTotalsCalculatorOutlet) {
      this.documentsTotalsCalculatorOutlet.recalculate()
    }
  }

  // Sync toggle checkbox state
  #syncToggleState(checked) {
    if (this.hasToggleTarget) {
      this.toggleTarget.checked = checked
    }
  }

  // Auto-update description with discount value, e.g. "Zľava na faktúru 10 %" or "Zľava na faktúru 100,00 EUR"
  // Only updates if description is empty, matches the default, or was previously auto-generated.
  #updateDescription() {
    if (!this.hasDescriptionTarget || !this.hasDefaultDescriptionValue) return

    const base = this.defaultDescriptionValue
    const current = this.descriptionTarget.value.trim()

    // Only auto-update if empty, matches default, or starts with default (previously auto-generated)
    if (current !== "" && current !== base && !current.startsWith(base)) return

    const locale = getUserLocale()

    if (this.discountTypeValue === "percentage") {
      const pct = parseDiscountPercent(this.percentTarget, locale)
      if (pct > 0) {
        this.descriptionTarget.value = `${base} ${formatNumber(pct, locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %`
      } else {
        this.descriptionTarget.value = base
      }
    } else {
      const amount = parseInputNumber(this.amountTarget, locale) || 0
      if (amount > 0) {
        const currency = (this.currencyValue || "EUR").toUpperCase()
        this.descriptionTarget.value = `${base} ${formatNumber(amount, locale, this.#currencyFractionOptions())} ${currency}`
      } else {
        this.descriptionTarget.value = base
      }
    }
  }

  // Mirror the document currency onto the [%|EUR] amount-mode toggle button.
  #syncCurrencyLabel() {
    if (!this.hasToggleAmountTarget) return
    this.toggleAmountTarget.textContent = (this.currencyValue || "EUR").toUpperCase()
  }

  // Sync visibility of percent/amount inputs and toggle button active states
  #syncVisibility() {
    const isAmount = this.discountTypeValue === "amount"

    if (this.hasPercentTarget) {
      this.percentTarget.classList.toggle("d-none", isAmount)
    }
    if (this.hasAmountTarget) {
      this.amountTarget.classList.toggle("d-none", !isAmount)
    }
    if (this.hasTogglePercentTarget) {
      this.togglePercentTarget.classList.toggle("btn-primary-light", !isAmount)
      this.togglePercentTarget.classList.toggle("btn-secondary", isAmount)
    }
    if (this.hasToggleAmountTarget) {
      this.toggleAmountTarget.classList.toggle("btn-primary-light", isAmount)
      this.toggleAmountTarget.classList.toggle("btn-secondary", !isAmount)
    }
  }

  #currencyFractionOptions() {
    const decimals = getCurrencyDecimals(this.currencyValue || this.documentCurrencySelect?.value || "EUR")
    return { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
  }

  // Check if any discount value is set (for connect-time notification)
  #hasAnyDiscount() {
    const locale = getUserLocale()
    if (this.hasPercentTarget && hasDiscount(this.percentTarget, locale)) return true
    if (this.hasAmountTarget && (parseInputNumber(this.amountTarget, locale) || 0) > 0) return true
    return false
  }
}
